import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";


// THE PRODUCT'S TOOLTIP, not MUI's default. 12px on #363636 with 8x12 of
// padding -- the same one the Overview cards use. MUI's own is 10px, which is
// a size nothing else in the app speaks.
import { PrimaryTooltip } from "../StyledComponent";
import { useTreeFab } from "./useTreeFab";
import {
    CHILD_ICON_SIZE,
    CHILD_SIZE,
    CANCEL_SIZE,
    CLOSE_EASE,
    DEFAULT_OFFSET,
    FAB_SIZE,
    TREE_FAB_COLORS,
    TREE_FAB_HALO,
    TREE_FAB_TYPE,
    arcPoints,
    labelGrowsLeft,
} from "./treeFabTokens";

// A floating action button that walks a TREE instead of firing one action.
//
// WHY A TREE AND NOT A MENU. The thing being chosen here is one decision made
// of several -- how many assets, then which movement -- and a flat list has to
// spell out every combination as its own row: two scopes times five movements
// is ten rows that all read almost the same. The arc asks one question at a
// time and keeps the answers on screen as a path, so the user always knows
// where they are and can step back one answer rather than starting over.
//
// THE STATE MACHINE IS NOT IN HERE. useTreeFab owns it, so the decisions can
// be tested without three animations and a portal -- see that file.
//
// GEOMETRY IN ONE PLACE. Every measurement comes from treeFabTokens, because
// the arc, the labels and the collapse target all have to agree about where
// the FAB's centre is; a stray literal here is how a child ends up flying back
// to a point the button is not on.

const noop = () => {};

// The anchor is a ZERO-SIZED box at the FAB's centre. Everything -- the arc,
// the halo, the finish card, the FAB itself -- is positioned from that one
// point, so there is a single number to change per corner rather than four
// different sets of edge offsets.
const anchorStyle = (position, offset) => {
    const centre = offset + FAB_SIZE / 2;
    const style = { position: "fixed", width: 0, height: 0 };
    if (position.startsWith("bottom")) style.bottom = centre;
    else style.top = centre;
    if (position.endsWith("right")) style.right = centre;
    else style.left = centre;
    return style;
};

// A pill whose icon sits at the corner end and whose label grows inward.
// row-reverse in the right-hand corners is the whole of it: the icon stays
// pinned to the edge and the text opens away from it.
const pillDirection = (position) =>
    labelGrowsLeft(position) ? "row-reverse" : "row";

const TreeFab = ({
    tree,
    position = "bottom-right",
    offset = DEFAULT_OFFSET,
    onFinish = noop,
    onChange,
    // The finish pill reads "<finishLabel> <path>", e.g.
    // "Create New: Work order / Single / Delivery".
    finishLabel = "Create New:",
    cancelLabel = "Cancel",
    value,
    defaultValue,
    motion = "spring",
    testId = "tree-fab",
    // Hidden entirely rather than disabled: a permanently dead floating button
    // is a control the user keeps trying.
    hidden = false,
    zIndex = 1100,
}) => {
    // The ONE place the media query is read. The hook takes the answer as a
    // flag so its timings -- which are also its state waits -- can be driven
    // from a test without a matchMedia stub.
    const prefersReducedMotion = useMediaQuery(
        "(prefers-reduced-motion: reduce)",
        { noSsr: true }
    );

    const fab = useTreeFab({
        tree,
        value,
        defaultValue,
        onChange,
        onFinish,
        motion,
        reducedMotion: prefersReducedMotion,
    });

    const {
        path, nodes, current, children, isLeaf, isOpen, phase, isBusy,
        promotingCode, pulse, timings, depth,
        toggleRoot, close, selectChild, back, reset, finish,
    } = fab;

    const [hovered, setHovered] = useState(null);
    const rootRef = useRef(null);
    const childRefs = useRef({});

    // Live means "open, settled, and taking clicks". Mid-transition the
    // children are on screen but must not be hit: a click landing on a pill
    // that is already flying back to the FAB selects a node the user can no
    // longer see.
    const live = isOpen && phase === "idle";

    const points = useMemo(
        () => arcPoints(children.length, position),
        [children.length, position]
    );

    // ESCAPE IS BACK, not close. On a menu two levels deep, closing throws
    // away both answers; stepping back gives up one, which is what the key
    // means in every other nested control.
    useEffect(() => {
        if (hidden) return undefined;
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            if (!isOpen && !path.length) return;
            event.stopPropagation();
            back();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [hidden, isOpen, path.length, back]);

    // FOCUS FOLLOWS THE MENU: into the arc when it opens, back to the FAB when
    // it closes. Without this a keyboard user tabs from the button into the
    // page behind the scrim and has to tab all the way round to reach the
    // options that just appeared.
    const wasLive = useRef(false);
    useEffect(() => {
        if (live && !wasLive.current) {
            const first = children[0] && childRefs.current[children[0].code];
            first?.focus?.();
        } else if (!live && wasLive.current) {
            rootRef.current?.focus?.();
        }
        wasLive.current = live;
    }, [live, children]);

    const onChildKeyDown = useCallback(
        (event, index) => {
            // Arrow keys walk the ARC ORDER, which is the reading order the
            // eye already follows -- not the DOM order, which happens to match
            // only because the arc is rendered in order.
            const last = children.length - 1;
            let nextIndex = null;
            if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                nextIndex = index >= last ? 0 : index + 1;
            } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                nextIndex = index <= 0 ? last : index - 1;
            }
            if (nextIndex === null) return;
            event.preventDefault();
            childRefs.current[children[nextIndex]?.code]?.focus?.();
        },
        [children]
    );

    if (hidden || !tree) return null;

    const rootLabel = current?.label || "";
    const rootHovered = hovered === "root" && !isOpen && !isLeaf && !isBusy;
    // Has the user started? The cancel button appears the moment they have,
    // and nothing else depends on this now that the breadcrumb is gone.
    const walkStarted = isOpen || path.length > 0;

    // "Create New: Work order / Single / Delivery" -- the whole answer, in the
    // breadcrumb's own short labels so the pill and the bar say the same
    // words. Built here rather than by the caller because the caller does not
    // know where the walk stopped.
    const finishText = [
        finishLabel,
        nodes.map((node) => node.shortLabel || node.label).join(" / "),
    ]
        .filter(Boolean)
        .join(" ");
    const growsLeft = labelGrowsLeft(position);

    // --- the label that unfurls out of a pill -----------------------------
    const unfurlSx = (shown, maxWidth = 200) => ({
        maxWidth: shown ? `${maxWidth}px` : "0px",
        opacity: shown ? 1 : 0,
        paddingLeft: shown && !growsLeft ? "10px" : "0px",
        paddingRight: shown && growsLeft ? "10px" : "0px",
        marginLeft: shown && growsLeft ? "16px" : "0px",
        marginRight: shown && !growsLeft ? "16px" : "0px",
        overflow: "hidden",
        whiteSpace: "nowrap",
        transition:
            "max-width 300ms cubic-bezier(.2,.8,.2,1), opacity 180ms ease, "
            + "padding 300ms cubic-bezier(.2,.8,.2,1), margin 300ms cubic-bezier(.2,.8,.2,1)",
    });

    const childTransform = (point, index) => {
        // The pill's icon end is pinned to the arc point. translate(-100%) in
        // the right-hand corners puts the pill's RIGHT edge there, which is
        // where its icon is -- so the icon sits on the arc and the label grows
        // off it, rather than the whole pill being centred and drifting as the
        // text opens.
        const edge = growsLeft
            ? `translate(-100%, -50%)`
            : `translate(0, -50%)`;
        const seat = `translate(${point.x + (growsLeft ? CHILD_SIZE / 2 : -CHILD_SIZE / 2)}px, ${point.y}px)`;

        if (live) {
            return {
                transform: `${seat} ${edge} scale(1)`,
                opacity: 1,
                transition:
                    `transform ${timings.openDuration}ms ${timings.ease} ${index * timings.openStagger}ms, `
                    + `opacity 200ms ease ${index * timings.openStagger}ms, `
                    + "background 150ms, border-color 150ms",
            };
        }
        // The one being promoted collapses INTO the FAB rather than back to
        // its own seat, so the eye follows it to the button that is about to
        // become it.
        if (phase === "promote" && children[index]?.code === promotingCode) {
            return {
                transform: `translate(${growsLeft ? CHILD_SIZE / 2 : -CHILD_SIZE / 2}px, 0px) ${edge} scale(0.5)`,
                opacity: 0,
                transition:
                    `transform ${timings.promoteDuration}ms ${CLOSE_EASE}, `
                    + "opacity 280ms ease-in",
            };
        }
        const reverse = (children.length - 1 - index) * timings.closeStagger;
        return {
            transform: `translate(${growsLeft ? CHILD_SIZE / 2 : -CHILD_SIZE / 2}px, 0px) ${edge} scale(0.2)`,
            opacity: 0,
            transition:
                `transform ${timings.closeDuration}ms ${CLOSE_EASE} ${reverse}ms, `
                + `opacity 200ms ease ${reverse}ms`,
        };
    };

    return (
        <>
            {/* SCRIM. Closes on click and keeps the path -- dismissing a menu
                is not the same as abandoning the answers already given. */}
            <Box
                data-testid={`${testId}-scrim`}
                onClick={close}
                sx={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: TREE_FAB_COLORS.scrim,
                    opacity: live ? 1 : 0,
                    pointerEvents: live ? "auto" : "none",
                    transition: "opacity 300ms ease",
                    // DELIBERATELY BELOW THE GLOBAL TOOLBAR (zIndex 9998), so
                    // the nav bar stays lit and clickable with the arc open.
                    // Covering it would mean a floating button's scrim taking
                    // the whole application hostage; leaving the page is a
                    // legitimate way to abandon a half-finished walk, and the
                    // component unmounts with the route anyway.
                    zIndex: zIndex - 1,
                }}
            />

            {/* THE ANCHOR: a zero-sized point at the FAB's centre. */}
            <Box sx={{ ...anchorStyle(position, offset), zIndex }}>
                {/* HALO. Not decoration -- it is what makes the arc read as
                    belonging to the button rather than as five loose pills. */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        pointerEvents: "none",
                        background: TREE_FAB_HALO,
                        transform: `translate(-50%,-50%) scale(${
                            isOpen && phase !== "swap" ? 1 : 0.2
                        })`,
                        opacity: isOpen || phase === "promote" ? 1 : 0,
                        transition:
                            "transform 600ms cubic-bezier(.2,.8,.2,1), opacity 400ms ease",
                    }}
                />

                {children.map((child, index) => {
                    const point = points[index] || { x: 0, y: 0 };
                    const motionSx = childTransform(point, index);
                    const isHovered = hovered === child.code && live;
                    return (
                        <PrimaryTooltip
                            key={child.code}
                            // The pill shows its label on hover; the tooltip is
                            // for TOUCH, where there is no hover to show it
                            // with, and for screen readers.
                            title={child.label}
                            disableHoverListener
                            // FOCUS MUST NOT OPEN IT EITHER. The arc moves
                            // focus to the first child the moment it opens, so
                            // a focus-triggered tooltip would fire on every
                            // open without anyone hovering or tapping. This
                            // tooltip exists for TOUCH; the accessible name is
                            // the button's own aria-label.
                            disableFocusListener
                            enterTouchDelay={0}
                            // DESCRIBES, does not NAME. Without this MUI clones
                            // the wrapper and puts its own aria-label on it,
                            // so the same option is announced twice -- once
                            // from the span and once from the button that
                            // actually carries the action.
                            describeChild
                        >
                            {/* A SPAN, because MUI's Tooltip cannot attach to a
                                disabled button -- a disabled element fires no
                                events, so the tooltip silently stops working
                                on exactly the devices it exists for, and MUI
                                warns on every render. The span is static, so
                                the absolutely-positioned button inside it
                                still resolves against the anchor. */}
                            <Box
                                component="span"
                                // NOT A BARE SPAN. An inline element whose only
                                // child is absolutely positioned has a
                                // zero-sized box at the anchor's origin, and
                                // MUI anchors the popper to THAT -- so every
                                // option's tooltip appeared over the FAB
                                // instead of over the option. The cancel
                                // button's wrapper already carried this.
                                sx={{ display: "flex" }}
                            >
                            <ButtonBase
                                ref={(node) => {
                                    childRefs.current[child.code] = node;
                                }}
                                data-testid={`${testId}-child-${child.code}`}
                                aria-label={child.label}
                                // ONLY the node's own disabled state. Being
                                // mid-transition is not a disabled button: the
                                // sx below already sets pointerEvents: none
                                // for that, and tabIndex takes it out of the
                                // tab order, so nothing is clickable or
                                // reachable while the arc is closing.
                                disabled={Boolean(child.disabled)}
                                tabIndex={live ? 0 : -1}
                                onClick={() => selectChild(child.code)}
                                onKeyDown={(event) => onChildKeyDown(event, index)}
                                onMouseEnter={() => setHovered(child.code)}
                                onMouseLeave={() =>
                                    setHovered((code) =>
                                        code === child.code ? null : code
                                    )
                                }
                                onFocus={() => setHovered(child.code)}
                                onBlur={() =>
                                    setHovered((code) =>
                                        code === child.code ? null : code
                                    )
                                }
                                sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    height: CHILD_SIZE,
                                    display: "flex",
                                    flexDirection: pillDirection(position),
                                    alignItems: "center",
                                    p: 0,
                                    borderRadius: "999px",
                                    border: `1px solid ${
                                        isHovered
                                            ? TREE_FAB_COLORS.primary
                                            : TREE_FAB_COLORS.line
                                    }`,
                                    backgroundColor: isHovered
                                        ? TREE_FAB_COLORS.tint
                                        : TREE_FAB_COLORS.surface,
                                    boxShadow: TREE_FAB_COLORS.childShadow,
                                    zIndex: isHovered ? 3 : 1,
                                    pointerEvents: live ? "auto" : "none",
                                    opacity: child.disabled ? 0.5 : undefined,
                                    ...motionSx,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: CHILD_ICON_SIZE,
                                        height: CHILD_ICON_SIZE,
                                        flex: "none",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: isHovered
                                            ? TREE_FAB_COLORS.primary
                                            : "transparent",
                                        color: isHovered
                                            ? TREE_FAB_COLORS.surface
                                            : TREE_FAB_COLORS.primaryDeep,
                                        transition: "background 150ms, color 150ms",
                                        "& svg": { fontSize: 20 },
                                    }}
                                >
                                    {child.icon}
                                </Box>
                                <Typography
                                    component="span"
                                    sx={{
                                        ...unfurlSx(isHovered),
                                        ...TREE_FAB_TYPE.childLabel,
                                        color: TREE_FAB_COLORS.ink,
                                        textTransform: "none",
                                    }}
                                >
                                    {child.label}
                                </Typography>
                            </ButtonBase>
                            </Box>
                        </PrimaryTooltip>
                    );
                })}

                {/* THE FAB AND ITS CANCEL SIT IN ONE ROW, anchored at the
                    corner. A flex row rather than two absolutely-positioned
                    buttons, because the create pill is as wide as its own text
                    -- "Create New: Work order / Single / Delivery" -- and
                    placing the cancel beside it any other way would mean
                    measuring that width in JavaScript and re-measuring it
                    whenever the path changed. Layout already knows. */}
                <Box
                    sx={{
                        position: "absolute",
                        right: growsLeft ? -FAB_SIZE / 2 : "auto",
                        left: growsLeft ? "auto" : -FAB_SIZE / 2,
                        top: -FAB_SIZE / 2,
                        display: "flex",
                        // row-reverse in the right corners puts the cancel to
                        // the LEFT of the pill: outboard, away from the thumb
                        // that was just travelling toward Create.
                        flexDirection: growsLeft ? "row-reverse" : "row",
                        alignItems: "center",
                        gap: "12px",
                    }}
                >
                <ButtonBase
                    ref={rootRef}
                    data-testid={isLeaf ? `${testId}-create` : `${testId}-root`}
                    aria-label={isLeaf ? finishText : rootLabel}
                    aria-expanded={isOpen}
                    aria-haspopup={isLeaf ? undefined : "menu"}
                    onClick={isLeaf ? finish : toggleRoot}
                    onMouseEnter={() => setHovered("root")}
                    onMouseLeave={() =>
                        setHovered((code) => (code === "root" ? null : code))
                    }
                    sx={{
                        flex: "none",
                        height: FAB_SIZE,
                        display: "flex",
                        flexDirection: pillDirection(position),
                        alignItems: "center",
                        p: 0,
                        borderRadius: "999px",
                        border: 0,
                        backgroundColor: isLeaf
                            ? TREE_FAB_COLORS.success
                            : TREE_FAB_COLORS.primary,
                        color: TREE_FAB_COLORS.surface,
                        boxShadow: TREE_FAB_COLORS.fabShadow,
                        transform: `scale(${pulse ? 0.88 : 1})`,
                        transformOrigin: growsLeft
                            ? `calc(100% - ${FAB_SIZE / 2}px) 50%`
                            : `${FAB_SIZE / 2}px 50%`,
                        transition:
                            "transform 280ms cubic-bezier(.34,1.56,.64,1), background 240ms ease",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: FAB_SIZE,
                            height: FAB_SIZE,
                            flex: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "& svg": { fontSize: 24 },
                        }}
                    >
                        {/* AT THE FINISH, THE ICON THE WALK STARTED UNDER --
                            tree.icon, not the leaf's and not a tick. A tick
                            acknowledges something already done; this button
                            has not done anything yet. Keeping the root's mark
                            also makes the finished pill read as the same
                            control the user pressed three clicks ago, grown
                            into its answer. */}
                        {/* ALWAYS THE NODE'S OWN ICON. It used to turn into a
                            cross while open, which put TWO crosses on screen
                            beside each other -- this one meaning "close the
                            arc" and the red one meaning "abandon the walk".
                            Two identical marks, two different consequences.
                            The red button is the only cross now. */}
                        {isLeaf ? tree.icon : current?.icon}
                        {/* HOW DEEP, on the button itself. Collapsed, the FAB
                            is the only thing on screen, and without this a
                            half-finished walk looks exactly like a fresh one.
                            Hidden on a leaf, where the card says it in full. */}
                        {depth > 0 && !isLeaf && (
                            <Box
                                data-testid={`${testId}-depth`}
                                sx={{
                                    position: "absolute",
                                    top: -3,
                                    right: -3,
                                    minWidth: 20,
                                    height: 20,
                                    px: "6px",
                                    borderRadius: "999px",
                                    backgroundColor: TREE_FAB_COLORS.surface,
                                    color: TREE_FAB_COLORS.primaryDeep,
                                    ...TREE_FAB_TYPE.depthBadge,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: TREE_FAB_COLORS.badgeShadow,
                                }}
                            >
                                {depth}
                            </Box>
                        )}
                    </Box>
                    {/* ALWAYS OPEN AT THE FINISH. While walking, the label
                        unfurls on hover so the collapsed button stays a
                        button; at the finish the words ARE the confirmation
                        and hiding them behind a hover would put the thing
                        being confirmed out of sight. */}
                    <Typography
                        component="span"
                        sx={{
                            ...unfurlSx(isLeaf || rootHovered, isLeaf ? 420 : 220),
                            ...TREE_FAB_TYPE.trigger,
                            letterSpacing: "0.01em",
                            textTransform: "none",
                        }}
                    >
                        {isLeaf ? finishText : rootLabel}
                    </Typography>
                </ButtonBase>

                {/* CANCEL. Present for the WHOLE walk, not only at the finish:
                    once a path exists the user is mid-task, and the way out
                    should not be something they have to reach the end to find.

                    It RESETS rather than stepping back one level -- stepping
                    back is what Escape does. Round and outboard of the pill,
                    so the destructive half of the pair is never the one under
                    a thumb travelling toward Create. */}
                {walkStarted && (
                    <PrimaryTooltip title={cancelLabel} describeChild>
                        <Box component="span" sx={{ display: "flex" }}>
                            <ButtonBase
                                data-testid={`${testId}-cancel`}
                                aria-label={cancelLabel}
                                onClick={reset}
                                sx={{
                                    flex: "none",
                                    width: CANCEL_SIZE,
                                    height: CANCEL_SIZE,
                                    borderRadius: "50%",
                                    backgroundColor: TREE_FAB_COLORS.cancel,
                                    color: TREE_FAB_COLORS.surface,
                                    boxShadow: TREE_FAB_COLORS.cancelShadow,
                                    "& svg": { fontSize: 20 },
                                    "&:hover": {
                                        backgroundColor: TREE_FAB_COLORS.cancelDeep,
                                    },
                                }}
                            >
                                <CloseIcon />
                            </ButtonBase>
                        </Box>
                    </PrimaryTooltip>
                )}
                </Box>
            </Box>
        </>
    );
};



export default TreeFab;
